import {
  STATUSES,
  MIN_SQFT,
  MAX_SQFT,
  HOME_TYPES,
  MAX_YEAR_BUILT,
  MIN_YEAR_BUILT,
  PROPERTY_TYPES,
  HOME_TYPES_ALL,
  LISTING_STATUSES,
  HOME_TYPES_HOUSE,
  MIN_PRICE_DOLLARS,
  MAX_PRICE_DOLLARS,
  HOME_TYPES_LOTLAND,
  MIN_BEDROOMS_COUNT,
  MIN_BATHROOMS_COUNT,
  HOME_TYPES_APTCONDO,
  HOME_TYPES_TOWNHOUSE,
} from '../../../constants/listings'
import history from '../../../history'
import { Form } from '../..'
import { omitBy, pick } from 'lodash'
import { useSelector } from 'react-redux'
import { FormEvent, useContext } from 'react'
import { FiltersHeader } from './FiltersHeader'
import { defineMessages, useIntl } from 'react-intl'
import { PATH_HELPERS } from '../../../constants/navigation'
import { FiltersFormData } from '../../../types/filtersTypes'
import { selectFilter } from '../../../store/selectors/filters'
import { ListingStatusEnum } from '../../../types/listingStatus'
import { FiltersFooterButtons } from './FiltersFooterButtons'
import { Price } from './Price'
import { Rooms } from './Rooms'
import DollarInput from '../../shared/Input/DollarInput'
import { HomeType } from './HomeType'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { BuildingSize } from './BuildingSize'
import { ListingStatus } from './ListingStatus'
import { ConstructionYear } from './ConstructionYear'
import {
  constructFilters,
  constructFiltersAndOmitEmpty,
} from '../../../helpers/listings/filters'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

import styles from './Filters.module.scss'

const FIELDS = [
  MIN_YEAR_BUILT,
  MAX_YEAR_BUILT,
  HOME_TYPES_ALL,
  HOME_TYPES_HOUSE,
  HOME_TYPES_LOTLAND,
  MIN_BEDROOMS_COUNT,
  MIN_BATHROOMS_COUNT,
  HOME_TYPES_APTCONDO,
  HOME_TYPES_TOWNHOUSE,
  ListingStatusEnum.ACTIVE,
  ListingStatusEnum.PENDING,
  { name: MIN_SQFT, parse: BuildingSize.parseValue },
  { name: MAX_SQFT, parse: BuildingSize.parseValue },
  { name: MIN_PRICE_DOLLARS, parse: DollarInput.parseValue },
  { name: MAX_PRICE_DOLLARS, parse: DollarInput.parseValue },
]

export const MSG = defineMessages({
  bed: {
    id: 'HomeSearch.listings.filters.beds.heading',
    defaultMessage: 'Beds',
  },
  bath: {
    id: 'HomeSearch.listings.filters.baths.heading',
    defaultMessage: 'Baths',
  },
})

export interface FiltersProps {
  onClose: () => void
}

export const Filters = ({ onClose }: FiltersProps) => {
  const intl = useIntl()
  const filters = useSelector(selectFilter)
  const { ampli } = useContext(AmplitudeContext)
  const customerId = useSelector(selectCustomerId)

  const handleSubmit = (_: FormEvent, data: FiltersFormData) => {
    const types = omitBy(pick(data, HOME_TYPES), value => !value)
    const statuses = omitBy(pick(data, LISTING_STATUSES), value => !value)

    data[PROPERTY_TYPES] = Object.keys(types)
    data[STATUSES] = Object.keys(statuses) as ListingStatusEnum[]

    const parsedData = constructFilters(data)

    if (parsedData.minPriceCents !== filters.minPriceCents) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_PRICE_MIN,
      })
    }

    if (parsedData.maxPriceCents !== filters.maxPriceCents) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_PRICE_MAX,
      })
    }

    if (parsedData.minBedroomsCount !== filters.minBedroomsCount) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_BED_MIN,
      })
    }

    if (parsedData.minBathroomsCount !== filters.minBathroomsCount) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_BATH_MIN,
      })
    }

    if (parsedData.minYearBuilt !== filters.minYearBuilt) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_YR_MIN,
      })
    }

    if (parsedData.maxYearBuilt !== filters.maxYearBuilt) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_YR_MAX,
      })
    }

    if (parsedData.minSqft !== filters.minSqft) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_SQFT_MIN,
      })
    }

    if (parsedData.maxSqft !== filters.maxSqft) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_SQFT_MAX,
      })
    }

    const dataPropertyTypes = JSON.stringify(data.propertyTypes.sort())
    const filterPropertyTypes = JSON.stringify(filters.propertyTypes.sort())
    if (dataPropertyTypes !== filterPropertyTypes) {
      ampli?.searchModified({
        search_filter_added: AmplitudeEventConstants.FILTER_PROPERTY_TYPE,
      })
    }

    history.push(
      `${PATH_HELPERS.listings.buildPath(
        customerId,
        filters.location.level,
        filters.location.name,
        constructFiltersAndOmitEmpty(data)
      )}`
    )

    onClose()
  }

  return (
    <Form className={styles.form} onSubmit={handleSubmit} fields={FIELDS}>
      <FiltersHeader />
      <Price />
      <Rooms name={MIN_BEDROOMS_COUNT} title={intl.formatMessage(MSG.bed)} />
      <Rooms name={MIN_BATHROOMS_COUNT} title={intl.formatMessage(MSG.bath)} />
      <HomeType />
      <BuildingSize />
      <ConstructionYear />
      <ListingStatus />
      <FiltersFooterButtons onClose={onClose} />
    </Form>
  )
}
